import type { Patient } from '../../../types';
import { Markdown } from '../../../components/Markdown/Markdown';
import { PathologyDetails } from './components/PathologyDetails';
import './PathologyResultsSummary.scss';

interface PathologyResultsSummaryProps {
  patient: Patient;
}

/**
 * Render the reference pathology report using the structured extraction payload.
 *
 * @param props - Patient hydrated with a pathology report.
 * @returns The pathology clinical summary.
 */
export function PathologyResultsSummary({ patient }: PathologyResultsSummaryProps) {
  const pathology = patient.pathologyReport;
  const narrative = patient.narrative;

  if (!pathology) {
    return (
      <div
        className="rounded-lg p-6 shadow-sm"
        style={{
          backgroundColor: 'var(--color-card)',
          border: '1px solid var(--color-border)',
        }}
      >
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          No pathology report available for this patient.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Single Unified Pathology Component - Including Report Summary and All Details */}
      <PathologyDetails
        diagnosis={patient.diagnosis}
        diagnosisSummary={pathology.diagnosisSummary}
        biopsySite={pathology.biopsySite}
        biopsyDate={pathology.biopsyDate}
        surgeryDate={pathology.surgeryDate}
        tumorGrade={pathology.tumorGrade}
        marginStatus={pathology.marginStatus}
        cytopathologicFeatures={pathology.cytopathologicFeatures}
        immunohistochemistry={pathology.immunohistochemistry}
        specialStains={pathology.specialStains}
        pathologyKeywords={pathology.pathologyKeywords}
      />
      {narrative && (
        <div className="pathology-narrative-section">
          <h3 className="pathology-narrative-title">Pathology Summary</h3>
          <div className="pathology-narrative-box">
            <Markdown className="pathology-narrative">{narrative}</Markdown>
          </div>
        </div>
      )}
    </div>
  );
}
